import { useState, useMemo, useEffect } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRoute, useLocation, Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EditContactModal from "@/components/modals/edit-contact-modal";
import NotesSection from "@/components/notes/notes-section";
import { useToast } from "@/hooks/use-toast";
import { 
  User, Mail, Phone, Building2, Edit, FileText, 
  ExternalLink, Briefcase, Download, Plus
} from "lucide-react";
import CreateWorkOrderModal from "@/components/modals/create-work-order-modal";
import type { User as UserType } from "@shared/schema";
import { format } from "date-fns";
import { formatDateOnly } from "@/lib/dateUtils";
import { useAuth } from "@/hooks/useAuth";
import AuditInfo from "@/components/audit-info";
import { track } from "@/lib/analytics";
import { formatPhoneNumber } from "@/lib/utils";

interface Carrier {
  id: number;
  name: string;
  docketPrefix: string;
  docketNumber: string;
  commonAuthorityStatus?: string;
}

interface CompanyRelation {
  id: number;
  companyId: number;
  relationshipType: string;
  isPrimary: boolean;
  company: {
    id: number;
    name: string;
    type: string;
    dotProfile?: {
      dotNumber?: string;
    } | null;
  };
  carriers: Carrier[];
}

interface ContactDetails {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  department: string;
  createdAt: string;
  updatedAt: string;
  companies: CompanyRelation[];
  workOrders: any[];
  authorityLetters: any[];
  createdByUser?: { firstName?: string | null; lastName?: string | null } | null;
  updatedByUser?: { firstName?: string | null; lastName?: string | null } | null;
}

export default function ContactDetailsPage() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [, setLocation] = useLocation();
  const [match, params] = useRoute("/contacts/:id");
  const contactId = params?.id ? parseInt(params.id) : null;
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateWorkOrderModal, setShowCreateWorkOrderModal] = useState(false);
  const [workOrderSearchTerm, setWorkOrderSearchTerm] = useState("");
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState("all");
  
  // Use unified endpoint that loads all data in a single request
  const { data: contactDetails, isLoading } = useQuery<ContactDetails>({
    queryKey: ["/api/contacts", contactId, "details"],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/contacts/${contactId}/details`);
      return response.json();
    },
    enabled: !!contactId,
    retry: false,
    staleTime: 30000,
  });
  
  // Fetch users for work order assignment
  const { data: users } = useQuery<UserType[]>({
    queryKey: ["/api/assignable-users"],
  });

  // Extract data from unified response
  const contact = contactDetails;
  const companies = contactDetails?.companies || [];
  const workOrders = contactDetails?.workOrders || [];
  const authorityLetters = contactDetails?.authorityLetters || [];

  const contactPageTitle = contact
    ? [
        `${contact.firstName} ${contact.lastName}`,
        companies.length > 0 && companies[0]?.company?.name ? companies[0].company.name : null,
      ].filter(Boolean).join(' - ')
    : null;
  usePageTitle(contactPageTitle);

  const updateContactMutation = useMutation({
    mutationFn: async (contactData: any) => {
      await apiRequest("PUT", `/api/contacts/${contactId}`, contactData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts", contactId, "details"] });
      toast({
        title: "Success",
        description: "Contact updated successfully",
      });
      setShowEditModal(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update contact",
        variant: "destructive",
      });
    },
  });

  const createWorkOrderMutation = useMutation({
    mutationFn: async (workOrderData: any) => {
      const response = await apiRequest("POST", "/api/work-orders", workOrderData);
      return response.json();
    },
    onSuccess: (createdWorkOrder) => {
      track("work_order_created", {
        source_page: `/contacts/${contactId}`,
        work_order_id: createdWorkOrder?.id ?? null,
        company_id: createdWorkOrder?.companyId ?? null,
        carrier_id: createdWorkOrder?.carrierId ?? null,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts", contactId, "details"] });
      toast({
        title: "Success",
        description: "Work order created successfully",
      });
      setShowCreateWorkOrderModal(false);
      if (createdWorkOrder?.id) {
        setLocation(`/work-orders/${createdWorkOrder.id}`);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create work order",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (showCreateWorkOrderModal) {
      track("work_order_create_opened", {
        source_page: `/contacts/${contactId}`,
      });
    }
  }, [showCreateWorkOrderModal, contactId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      // Client-action group (amber)
      case "collecting_inputs":
      case "awaiting_payment":
      case "awaiting_signatures":
      case "pending":
        return "bg-amber-100 text-amber-800";
      // Internal group (blue)
      case "new":
      case "validating":
      case "preparing_documents":
      case "work_complete":
      case "in_progress":
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "awaiting_client_filing":
        return "bg-amber-100 text-amber-800";
      // External group (purple)
      case "submitted":
        return "bg-purple-100 text-purple-800";
      // Terminal group (gray)
      case "closed":
      case "cancelled":
        return "bg-gray-100 text-gray-500";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      owner: "bg-amber-100 text-amber-800",
      billing: "bg-green-100 text-green-800",
      operations: "bg-blue-100 text-blue-800",
      safety: "bg-orange-100 text-orange-800",
      dispatch: "bg-purple-100 text-purple-800",
      compliance: "bg-indigo-100 text-indigo-800",
      emergency: "bg-red-100 text-red-800",
      other: "bg-gray-100 text-gray-800",
    };
    return colors[role] || colors.other;
  };

  const filteredWorkOrders = useMemo(() => {
    return workOrders.filter((wo: any) => {
      const matchesSearch = 
        wo.workOrderNumber?.toLowerCase().includes(workOrderSearchTerm.toLowerCase()) ||
        wo.serviceType?.toLowerCase().includes(workOrderSearchTerm.toLowerCase()) ||
        wo.description?.toLowerCase().includes(workOrderSearchTerm.toLowerCase());
      const matchesStatus = workOrderStatusFilter === "all" || wo.status === workOrderStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [workOrders, workOrderSearchTerm, workOrderStatusFilter]);

  if (isLoading) {
    return (
      <ResponsiveLayout title="Loading..." subtitle="" currentPage="contacts" isLoading={true}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </ResponsiveLayout>
    );
  }

  if (!contact) {
    return (
      <ResponsiveLayout 
        title="Contact Not Found" 
        subtitle="" 
        currentPage="contacts"
        breadcrumbs={[{ label: "Contacts", href: "/contacts" }, { label: "Not Found" }]}
      >
        <div className="text-center py-12">
          <p className="text-gray-500">The contact you're looking for doesn't exist.</p>
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout 
      title={`${contact.firstName} ${contact.lastName}`} 
      subtitle="Contact Details" 
      currentPage="contacts"
      breadcrumbs={[
        { label: "Contacts", href: "/contacts" },
        { label: `${contact.firstName} ${contact.lastName}` }
      ]}
    >
      <div className="mb-6 flex items-center justify-between gap-2">
        <AuditInfo
          createdAt={contact.createdAt}
          createdBy={contact.createdByUser}
          updatedAt={contact.updatedAt}
          updatedBy={contact.updatedByUser}
        />
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => setShowCreateWorkOrderModal(true)} 
            variant="outline"
            data-testid="button-create-work-order"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Work Order
          </Button>
          <Button onClick={() => setShowEditModal(true)} data-testid="button-edit-contact">
            <Edit className="w-4 h-4 mr-2" />
            Edit Contact
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{contact.firstName} {contact.lastName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                {contact.email ? (
                  <a href={`mailto:${contact.email}`} className="font-medium text-blue-600 hover:underline inline-flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {contact.email}
                  </a>
                ) : (
                  <p className="text-gray-400">Not provided</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                {contact.phone ? (
                  <a href={`tel:${contact.phone}`} className="font-medium text-blue-600 hover:underline inline-flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {formatPhoneNumber(contact.phone)}
                  </a>
                ) : (
                  <p className="text-gray-400">Not provided</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Job Title</p>
                <p className="font-medium">{contact.jobTitle || "Not specified"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Department</p>
                <p className="font-medium">{contact.department || "Not specified"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">{format(new Date(contact.createdAt), "MMM d, yyyy")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Companies Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Companies ({companies.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {companies.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 dark:bg-gray-800">
                      <th className="text-left p-3 font-medium text-gray-600">Company</th>
                      <th className="text-left p-3 font-medium text-gray-600">DOT #</th>
                      <th className="text-left p-3 font-medium text-gray-600">Role</th>
                      <th className="text-left p-3 font-medium text-gray-600">Carriers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((companyRel) => (
                      <tr key={companyRel.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800" data-testid={`row-company-${companyRel.companyId}`}>
                        <td className="p-3">
                          <Link href={`/companies/${companyRel.companyId}`}>
                            <span className="font-medium text-blue-600 hover:underline cursor-pointer inline-flex items-center gap-1">
                              {companyRel.company.name}
                              <ExternalLink className="w-3 h-3" />
                            </span>
                          </Link>
                        </td>
                        <td className="p-3 text-gray-600">
                          {companyRel.company.dotProfile?.dotNumber || "—"}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Badge className={getRoleBadgeColor(companyRel.relationshipType)}>
                              {companyRel.relationshipType}
                            </Badge>
                            {companyRel.isPrimary && (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Primary</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          {companyRel.carriers && companyRel.carriers.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {companyRel.carriers.map((carrier) => (
                                <Link key={carrier.id} href={`/carriers/${carrier.id}`}>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 cursor-pointer">
                                    {carrier.name}
                                    {carrier.docketNumber && (
                                      <span className="text-blue-500">({carrier.docketPrefix}-{carrier.docketNumber})</span>
                                    )}
                                    <ExternalLink className="w-3 h-3" />
                                  </span>
                                </Link>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No company associations</p>
            )}
          </CardContent>
        </Card>

        {/* Work Orders Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Work Orders ({workOrders.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {workOrders.length > 0 && (
              <div className="flex items-center gap-4 mb-4">
                <Input
                  placeholder="Search work orders..."
                  value={workOrderSearchTerm}
                  onChange={(e) => setWorkOrderSearchTerm(e.target.value)}
                  className="max-w-xs"
                  data-testid="input-search-work-orders"
                />
                <Select value={workOrderStatusFilter} onValueChange={setWorkOrderStatusFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="select-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="collecting_inputs">Collecting Inputs</SelectItem>
                    <SelectItem value="validating">Validating</SelectItem>
                    <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                    <SelectItem value="preparing_documents">Preparing Documents</SelectItem>
                    <SelectItem value="awaiting_signatures">Awaiting Signatures</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="awaiting_client_filing">Awaiting Client Filing</SelectItem>
                    <SelectItem value="work_complete">Work Complete</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {filteredWorkOrders.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {filteredWorkOrders.map((wo: any) => (
                  <div key={wo.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Link href={`/work-orders/${wo.id}`}>
                          <span className="font-medium text-blue-600 hover:underline cursor-pointer inline-flex items-center gap-1">
                            {wo.workOrderNumber || `WO-${wo.id}`}
                            <ExternalLink className="w-3 h-3" />
                          </span>
                        </Link>
                        <Badge className={getStatusColor(wo.status)}>
                          {wo.status?.replace("_", " ")}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {wo.serviceTypeName || wo.serviceType}
                        {wo.company && ` • ${wo.company.name}`}
                      </p>
                      <p className="text-xs text-gray-400">
                        Created: {format(new Date(wo.createdAt), "MMM d, yyyy")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                {workOrders.length === 0 
                  ? "No work orders found for this contact" 
                  : "No work orders match your filters"}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Authority Letters Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Authority Letters ({authorityLetters.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {authorityLetters.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {authorityLetters.map((letter: any) => (
                  <div key={letter.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <Badge className={
                        letter.letterType === "Authority Grant" ? "bg-green-100 text-green-800 border-green-200" :
                        letter.letterType === "Reinstatement" ? "bg-blue-100 text-blue-800 border-blue-200" :
                        letter.letterType === "Name Change" ? "bg-purple-100 text-purple-800 border-purple-200" :
                        letter.letterType === "Transfer" ? "bg-amber-100 text-amber-800 border-amber-200" :
                        "bg-gray-100 text-gray-800 border-gray-200"
                      }>
                        {letter.letterType || "Unknown"}
                      </Badge>
                      {letter.authorityType && (
                        <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-2 py-0.5 rounded">
                          {letter.authorityType === 'A' ? 'CMO' : 
                           letter.authorityType === 'B' ? 'Contract' : 
                           letter.authorityType === 'C' ? 'Broker' : letter.authorityType}
                        </span>
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium text-sm">{letter.docketFull || "-"}</span>
                        {letter.usdotNumber && (
                          <span className="text-xs text-muted-foreground">DOT: {letter.usdotNumber}</span>
                        )}
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDateOnly(letter.serviceDate, "MMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {letter.workOrder && (
                        <Link href={`/work-orders/${letter.workOrder.id}`}>
                          <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                            WO #{letter.workOrder.id}
                          </Badge>
                        </Link>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await fetch(`/api/authority-letters/${letter.id}/download`, { credentials: "include" });
                            if (!response.ok) throw new Error("Download failed");
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `authority-letter-${letter.docketFull || letter.id}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            toast({ title: "Error", description: "Failed to download letter", variant: "destructive" });
                          }
                        }}
                        title="Download letter"
                        data-testid={`button-download-${letter.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No authority letters found for this contact</p>
            )}
          </CardContent>
        </Card>

        {/* Notes Section */}
        <NotesSection
          entityType="contact"
          entityId={String(contactId)}
          title="Notes"
        />
      </div>

      {showEditModal && contact && (
        <EditContactModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={(data) => updateContactMutation.mutate(data)}
          isLoading={updateContactMutation.isPending}
          contact={contact}
        />
      )}

      {showCreateWorkOrderModal && contact && (
        <CreateWorkOrderModal
          onClose={() => setShowCreateWorkOrderModal(false)}
          onSubmit={(data) => createWorkOrderMutation.mutate(data)}
          isLoading={createWorkOrderMutation.isPending}
          users={users || []}
          currentUserId={currentUser?.id}
          prePopulate={{
            contactId: contactId || undefined,
            contactName: `${contact.firstName} ${contact.lastName}`,
            companyId: companies.length > 0 ? companies[0].companyId : undefined,
          }}
        />
      )}
    </ResponsiveLayout>
  );
}
